import React, { createContext, useContext, useState, useEffect } from 'react';

export interface CustomAction {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
}

interface CustomizeLayoutModeContextType {
  customizeLayoutMode: boolean;
  setCustomizeLayoutMode: (enabled: boolean) => void;
  columnCount: number;
  setColumnCount: (count: number) => void;
  customActions: CustomAction[];
  setCustomActions: (actions: CustomAction[]) => void;
}

const CustomizeLayoutModeContext = createContext<CustomizeLayoutModeContextType | undefined>(undefined);

export const CustomizeLayoutModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [customizeLayoutMode, setCustomizeLayoutMode] = useState(false);
  const [columnCount, setColumnCount] = useState<number>(() => {
    const saved = localStorage.getItem('customizeLayoutColumnCount');
    return saved ? parseInt(saved) : 2;
  });
  const [customActions, setCustomActions] = useState<CustomAction[]>([]);

  // Save column count to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('customizeLayoutColumnCount', columnCount.toString());
  }, [columnCount]);

  return (
    <CustomizeLayoutModeContext.Provider
      value={{
        customizeLayoutMode,
        setCustomizeLayoutMode,
        columnCount,
        setColumnCount,
        customActions,
        setCustomActions,
      }}
    >
      {children}
    </CustomizeLayoutModeContext.Provider>
  );
};

export const useCustomizeLayoutMode = (): CustomizeLayoutModeContextType => {
  const context = useContext(CustomizeLayoutModeContext);
  if (!context) {
    throw new Error('useCustomizeLayoutMode must be used within a CustomizeLayoutModeProvider');
  }
  return context;
};
